import { PaymentMethod, PaymentState } from '@zooplus/checkout-state';

import { creditCardsFromCheckout } from '@config/consts';
import { Issuer, PaymentMethodStatus } from '@types-internal';
import { PreferredPayment } from '@types-internal/CheckoutTypes';
import {
  PaymentMethodCheckout,
  PaymentMethodShortName,
  flippedSupportedPMs,
  bancontactPaymentMethods,
} from '@types-internal/ModernizePaymentTypes';
import { TrackingFn } from '@types-internal/TrackingTypes';

/**
 * Utility functions for managing and formatting payment methods within a checkout or payment system.
 * Functions include:
 * - checkApplePaySupported
 * - formatPaymentLists
 * - generatePreferredPayment
 * - getInitialSelectedPayment
 * - getInitialPaymentMethods
 * - getPaymentMethodStatus
 * - isPaymentMethodDisabled
 * - onPaymentMethodClick
 * - separatePaymentLists
 * - trackChangePM
 *
 * PS. When adding new functions, please consider keeping them in alphabetical order.
 */

/**
 * Function to check whether Apple Pay is supported in the browser.
 * This function checks if the `ApplePaySession` is available in the `window` object
 * and if the browser can make payments using Apple Pay.
 *
 * canMakePayments() can still throw an error `Trying to start an Apple Pay session from an insecure document`,
 * so we need to catch it and return false
 *
 * @returns {boolean} True if Apple Pay is supported and can make payments, otherwise false.
 */

export const checkApplePaySupported = (): boolean => {
  const ApplePay = window.ApplePaySession;
  try {
    return !!(ApplePay && ApplePaySession.canMakePayments());
  } catch (e) {
    return false;
  }
};

/**
 * Function to return mock list of payments for each country with payment details
 *
 * @param countryToPaymentMethodMap list of countries and payment codes for each country
 * @param issuers the list of issuers for Ideal and Online Banking PL
 * @param disabledPayment the payment method should be disabled
 * @param autoshipmentEligiblePayment the list of autoshipment eligible payment methods
 * @param notAvailableForGuestPayments the list of payments that aren't available for guest
 * @param psp psp name
 * @param PMConfig payment setup info
 * @returns list of payments in a similar format that documented here
 */

export const formatPaymentLists = (
  countryToPaymentMethodMap: any,
  issuers: any,
  disabledPayment?: any,
  autoshipmentEligiblePayments?: any,
  notAvailableForGuestPayments?: any,
  psp?: string,
  PMConfig?: any,
  preferredPayment?: any,
) => {
  const countryCodes = Object.keys(countryToPaymentMethodMap);
  const paymentMethodList: any = {};
  const paymentSetupInfo = psp === 'ADYEN' ? PMConfig.Adyen : PMConfig.Braintree;
  const config: any = {
    OP: { issuers, lastIssuer: null },
    BC: { paymentSetupInfo: PMConfig.Bancontact },
    BS: { bankAccountDetails: { number: '1234', owner: 'John Doe' } },
    K: { paymentSetupInfo: paymentSetupInfo, cards: creditCardsFromCheckout },
  };

  countryCodes.forEach((country: any) => {
    paymentMethodList[country] = countryToPaymentMethodMap[country].map(
      (payment: PaymentMethodShortName, key: number) => ({
        paymentMethod: flippedSupportedPMs[payment],
        paymentMethodShortName: payment,
        autoshipmentEligible: autoshipmentEligiblePayments.includes(payment),
        availableForGuest: !notAvailableForGuestPayments.includes(payment),
        order: key + 1,
        preferredPayment: payment == preferredPayment,
        disabled: disabledPayment && disabledPayment.includes(payment),
        default: key == 0,
        details: config[payment] || {},
      }),
    );
  });

  return paymentMethodList;
};

/**
 * Function to return preferred payment object based on payment short name and some details
 *
 * @param paymentMethodShortName the payment short name
 * @param issuer the issuer info
 * @param creditCard the card Info
 * @returns preferred payment object
 */

export const generatePreferredPayment = (
  paymentMethod: PaymentMethodShortName,
  issuer?: Issuer,
  cards?: any,
  sepaInfo?: any,
) => {
  return {
    paymentMethod: flippedSupportedPMs[paymentMethod],
    paymentMethodShortName: paymentMethod,
    ...(issuer && { details: { lastIssuer: issuer } }),
    ...(cards && {
      details: {
        cards: cards,
      },
    }),
    ...(sepaInfo && {
      details: {
        bankAccountDetails: sepaInfo,
      },
    }),
  };
};

/**
 * Function to get the initial selected payment method.
 *
 * This function determines the initial selected payment method based on the following priority order:
 * 1. If autoshipment is enabled:
 *    - Preferred payment method eligible for autoshipment, if not disabled.
 *    - Default payment method eligible for autoshipment, if not disabled.
 *    - The first available autoshipment-eligible payment method, if no preferred or default payment methods are autoshipment eligible.
 * 2. If autoshipment is not enabled:
 *    - The preferred payment method if it's not disabled.
 *    - The default payment method if it's not disabled.
 *    - The first available payment method that is not disabled.
 *
 * @param paymentMethods - An array of available payment methods.
 * @param customerType - The type of customer: 'EC' (Existing Customer), 'NC' (New Customer), 'GC' (Guest Customer), or '' (Not Specified).
 * @param preferred - The preferred payment method identifier.
 * @param autoshipmentReady - Whether autoshipment is ready and eligible for processing.
 *
 * @returns The initially selected payment method, or `undefined` if no suitable payment method is found.
 */

export const getInitialSelectedPayment = (
  paymentMethods: PaymentMethodCheckout[],
  customerType: string = '',
  pciPaymentPageEnabled: boolean,
  preferred?: string,
  autoshipmentReady?: boolean,
) => {
  const defaultPayment = paymentMethods.find((pm) => pm.default);
  const preferredPayment = paymentMethods.find(
    (pm) => pm.paymentMethodShortName === preferred || pm.preferredPayment,
  );

  // If order autoshipment is eligible, prioritize eligible payment methods with autoshipment eligibility
  if (autoshipmentReady && !preferredPayment) {
    if (
      defaultPayment &&
      defaultPayment.autoshipmentEligible &&
      !isPaymentMethodDisabled(defaultPayment, customerType, pciPaymentPageEnabled)
    ) {
      return defaultPayment;
    }
    const nextAutoshipmentEligiblePayment = paymentMethods.find(
      (pm) =>
        pm.autoshipmentEligible &&
        !isPaymentMethodDisabled(pm, customerType, pciPaymentPageEnabled),
    );
    return nextAutoshipmentEligiblePayment;
  }

  // If autoshipment is not enabled, continue with standard priority

  if (
    preferredPayment &&
    !isPaymentMethodDisabled(preferredPayment, customerType, pciPaymentPageEnabled)
  ) {
    return preferredPayment;
  }
  if (
    defaultPayment &&
    !isPaymentMethodDisabled(defaultPayment, customerType, pciPaymentPageEnabled)
  ) {
    return defaultPayment;
  }

  const nextAvailablePaymentMethod = paymentMethods.find(
    (pm) => !isPaymentMethodDisabled(pm, customerType, pciPaymentPageEnabled),
  );
  return nextAvailablePaymentMethod;
};

/**
 * Function to get the initial payment methods
 *
 * This function transforms the initial payment state by:
 * 1. Applying the client key for Adyen which is stored locally and not provided by the checkout state
 * 2. Sets the Sepa IBAN to empty for the SEPA Direct Debit payment method as it only the last 4 digits are provided by the checkout state
 *
 * @param paymentState - Payment state object
 * @param adyenClientKey - Adyen client key
 * @returns The transformed payment state object
 */
export const getInitialPaymentMethods = (paymentState?: PaymentState, adyenClientKey?: string) => {
  if (!paymentState) return undefined;
  const { paymentMethods: initPaymentMethods = [] } = paymentState;

  const paymentMethods = initPaymentMethods?.map((paymentMethod) => {
    const constructedPM = paymentMethod;
    if (
      isPaymentMethodDisabled(
        paymentMethod,
        paymentState?.customerType,
        paymentState?.pciPaymentPageEnabled,
      )
    ) {
      return { ...paymentMethod, disabled: true };
    }
    if (
      constructedPM.paymentMethodShortName === 'K' &&
      constructedPM.details?.paymentSetupInfo?.clientInfo &&
      constructedPM.details?.paymentSetupInfo?.clientInfo?.pspName === 'ADYEN'
    ) {
      constructedPM.details.paymentSetupInfo.clientInfo.pspToken = adyenClientKey;
    }

    if (
      constructedPM.paymentMethodShortName === 'BS' &&
      constructedPM.details?.bankAccountDetails
    ) {
      constructedPM.details.bankAccountDetails.number = '';
    }
    return paymentMethod;
  });

  return {
    ...paymentState,
    paymentMethods,
  };
};

/**
 * Function to return status of payment method
 * Currently on the following 3 states are part of the payment method: 'default' | 'selected' | 'disabled' |
 * Tests would be added once the logic for getPaymentMethodStatus is finalized
 *
 * @param paymentMethod the payment tab should be displayed
 * @param selectedPayment the currently selected payment method
 * @param customerType the possible customer type values: EC, NC, GC, ''. This values are defined in shop-chekcout.
 * For more detailed info, please check https://src.private.zooplus.net/projects/CHK/repos/shop-checkout/browse/src/main/java/com/zooplus/dto/payment/CustomerType.java#13-16
 * @returns list of payments in a similar format that documented here
 */

export const getPaymentMethodStatus = (
  paymentMethod: PaymentMethodCheckout,
  selectedPayment: PaymentMethodCheckout | undefined,
  customerType: string = '',
  pciPaymentPageEnabled?: boolean,
  selectedCardIdentifier?: string,
): 'default' | 'selected' | 'disabled' => {
  if (isPaymentMethodDisabled(paymentMethod, customerType, pciPaymentPageEnabled)) {
    return 'disabled';
  } else if (
    paymentMethod.paymentMethodShortName === selectedPayment?.paymentMethodShortName &&
    (selectedPayment?.order ||
      bancontactPaymentMethods.includes(selectedPayment?.paymentMethodShortName || ''))
  ) {
    if (paymentMethod.paymentMethodShortName === 'K' && selectedCardIdentifier) {
      return 'default';
    } else {
      return 'selected';
    }
  }
  return 'default';
};

/**
 * Function to return the status of a special case for Bancontact cards and mobile payments
 *
 * @param paymentMethod the payment method to be displayed
 * @param selectedPayment the currently selected payment method
 * @param customerType the possible customer type values: EC, NC, GC, ''. This values are defined in shop-chekcout.
 * For more detailed info, please check https://src.private.zooplus.net/projects/CHK/repos/shop-checkout/browse/src/main/java/com/zooplus/dto/payment/CustomerType.java#13-16
 * @returns string Bancontact payment method status
 */

export const getBancontactPaymentMethodStatus = (
  paymentMethods: PaymentMethod[],
  selectedPayment: PaymentMethodCheckout | undefined,
  customerType: string = '',
  pciPaymentPageEnabled?: boolean,
) => {
  const BM = paymentMethods?.find((pm) => pm.paymentMethodShortName === 'BM');
  const BC = paymentMethods?.find((pm) => pm.paymentMethodShortName === 'BC');
  const BMPMStatus =
    BM && getPaymentMethodStatus(BM, selectedPayment, customerType, pciPaymentPageEnabled);
  const BCPMStatus =
    BC && getPaymentMethodStatus(BC, selectedPayment, customerType, pciPaymentPageEnabled);
  let BCStatus: PaymentMethodStatus = 'default';

  if (BMPMStatus === 'selected' || BCPMStatus === 'selected') BCStatus = 'selected';
  else if (BMPMStatus === 'disabled' && BCPMStatus === 'disabled') BCStatus = 'disabled';
  return BCStatus;
};

/**
 * Checks if a payment method is unavailable for guest customers.
 */
const isUnavailableForGuest = (availableForGuest: boolean, customerType: string): boolean => {
  return !availableForGuest && (customerType === 'GC' || customerType === '');
};

/**
 * Function to determine if a payment method is disabled.
 *
 * This function evaluates whether a given payment method should be disabled based on its properties
 * and the type of customer. The following checks are performed:
 * 1. If the payment method is explicitly marked as disabled.
 * 2. If the payment method is not available for guest customers and the customer type is either
 *    guest customer ('GC') or not specified ('').
 * 3. If the payment method is Apple Pay ('AP') and Apple Pay is not supported.
 *
 * @param paymentMethod - The payment method to be evaluated.
 * @param customerType - The type of customer, which can be 'EC' (Existing Customer), 'NC' (New Customer),
 *                       'GC' (Guest Customer), or '' (Not Specified). These values are defined in shop-checkout.
 *                       For more detailed info, please check
 *                       https://src.private.zooplus.net/projects/CHK/repos/shop-checkout/browse/src/main/java/com/zooplus/dto/payment/CustomerType.java#13-16
 * @returns boolean - Returns true if the payment method is disabled based on the evaluated conditions, otherwise false.
 */

export const isPaymentMethodDisabled = (
  paymentMethod: PaymentMethodCheckout,
  customerType: string = '',
  pciPaymentPageEnabled?: boolean,
): boolean => {
  const { disabled, availableForGuest, paymentMethodShortName } = paymentMethod;
  if (disabled) {
    return true;
  }
  if (isUnavailableForGuest(availableForGuest, customerType)) {
    return true;
  }
  if (paymentMethodShortName === 'AP' && !checkApplePaySupported()) {
    return true;
  }
  const pspToken = paymentMethod.details?.paymentSetupInfo?.clientInfo?.pspToken;
  if (['BC', 'K'].includes(paymentMethodShortName || '') && !pspToken && !pciPaymentPageEnabled) {
    return true;
  }
  return false;
};

/**
 * Function to select correct payment method based on the payment method status: 'default' | 'selected' | 'disabled'
 * Handles edge cases for Bancontact cards and mobile payments
 *
 * @param paymentMethods the payment methods list
 * @param paymentMethod the payment tab should be displayed
 * @param selectedPayment the currently selected payment method
 * @param customerType the possible customer type values: EC, NC, GC, ''. This values are defined in shop-chekcout.
 * For more detailed info, please check https://src.private.zooplus.net/projects/CHK/repos/shop-checkout/browse/src/main/java/com/zooplus/dto/payment/CustomerType.java#13-16
 * @returns void
 */

export const onPaymentMethodClick =
  (
    paymentMethods: PaymentMethodCheckout[],
    paymentMethod: PaymentMethodCheckout,
    selectedPayment: PaymentMethodCheckout | undefined,
    customerType: string = '',
    setSelectedPayment: (paymentMethod: any) => void,
    deselectCreditCard: (() => void) | undefined,
    pciPaymentPageEnabled?: boolean,
  ) =>
  () => {
    const { paymentMethodShortName } = paymentMethod;
    const isPaymentMethodDisabled =
      getPaymentMethodStatus(
        paymentMethod,
        selectedPayment,
        customerType,
        pciPaymentPageEnabled,
      ) === 'disabled';
    if (paymentMethodShortName === 'BM' && isPaymentMethodDisabled) {
      setSelectedPayment(paymentMethods.find((pm) => pm.paymentMethodShortName === 'BC'));
    } else if (paymentMethodShortName === 'BC' && isPaymentMethodDisabled) {
      setSelectedPayment(paymentMethods.find((pm) => pm.paymentMethodShortName === 'BM'));
    } else {
      setSelectedPayment(paymentMethod);
      deselectCreditCard?.();
    }
  };

/**
 * Separate autoshipment and non-autoshipment eligible payment methods into two different lists
 *
 * @param paymentMethodList list of payment methods with additional details
 * @returns autoshipment eligible and nonautoshipment eligible payment lists
 */
export const separatePaymentLists = (paymentMethodList: any[]) => {
  const eligible = paymentMethodList.filter((payment: any) => payment.autoshipmentEligible);
  const nonEligible = paymentMethodList.filter((payment: any) => !payment.autoshipmentEligible);
  return { eligible, nonEligible };
};

/**
 *
 * @param selectedPM Currenctly selected payment method
 * @param preferredPM preferred payment method saved in the checkout state
 * @param track track function
 * @returns void
 */
export const trackChangePM = (
  selectedPM?: PreferredPayment,
  preferredPM?: PreferredPayment,
  track?: TrackingFn,
) => {
  const { details: selectedPMdetails, paymentMethodShortName: selectedPMShortName } =
    selectedPM ?? {};
  const { details: preferredPMdetails, paymentMethodShortName: preferredPMShortName } =
    preferredPM ?? {};

  const { bankAccountDetails: selectedPMBankAccountDetails } = selectedPMdetails ?? {};
  const { number: selectedPMNumber, owner: selectedPMOwner } = selectedPMBankAccountDetails ?? {};

  const { bankAccountDetails: preferredPMBankAccountDetails } = preferredPMdetails ?? {};
  const { number: preferredPMNumber, owner: preferredPMOwner } =
    preferredPMBankAccountDetails ?? {};

  const isSamePMSelected = selectedPMShortName === preferredPMShortName;
  const isBSSelected = isSamePMSelected && selectedPMShortName === 'BS';

  if (!selectedPMShortName) {
    return;
  }

  if (
    (isBSSelected && selectedPMNumber && selectedPMNumber !== preferredPMNumber) ||
    (isBSSelected && selectedPMOwner && selectedPMOwner !== preferredPMOwner)
  ) {
    return track?.('ChangePaymentMethod', 'BS');
  }

  if (!isSamePMSelected && selectedPMShortName === 'K') {
    return track?.('ChangePaymentMethod', 'AK');
  }

  if (!isSamePMSelected) {
    return track?.('ChangePaymentMethod', selectedPMShortName);
  }
};
